/**
 * StopButton — aborts the live stream mid-flight (STORY-002-009, AC #7).
 *
 * Rendered in place of Send while `isStreaming` is true. Clicking it calls the
 * store's `stopStreaming()`, which aborts the AbortController, discards the
 * partial assistant message, and re-enables the input.
 *
 * ≥44px tap target (AC #15) and a localised aria-label.
 */
import { Square } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ReactElement } from "react";

export interface StopButtonProps {
  onStop: () => void;
}

/**
 * Geometry deliberately mirrors the Send button it replaces (same 44px box,
 * same 10px radius, same slot) so the swap mid-stream reads as one control
 * changing state rather than two controls trading places. It is tinted with the
 * error token rather than the brand: stopping is an interruption, and it should
 * not look like the affirmative action it sits on top of.
 */
export function StopButton({ onStop }: StopButtonProps): ReactElement {
  const { t } = useTranslation("chat");
  return (
    <button
      type="button"
      onClick={onStop}
      aria-label={t("cta.stopAria")}
      title={t("cta.stop")}
      data-testid="chat-stop"
      className="grid size-11 shrink-0 place-items-center rounded-[10px] bg-[color-mix(in_srgb,var(--g-error)_12%,transparent)] text-[var(--g-error)] transition-[background-color,transform] duration-150 hover:bg-[color-mix(in_srgb,var(--g-error)_20%,transparent)] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-error)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-surface)] motion-reduce:transition-none"
    >
      <Square className="size-3.5 fill-current" aria-hidden="true" />
    </button>
  );
}

export default StopButton;
